import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    useCreatePersonProjectEventMutation,
    useGetPersonProjectEventMutation,
    useUpdatePersonProjectEventMutation
} from 'services/person/event/event.endpoint';
import { PersonProjectEvent, PersonProjectEventFormik, PersonProjectEventPayload } from 'services/person/event/event.types';
import { useGetAllUsersMutation } from 'services/user/user.endpoint';
import { User } from 'types/User';
import { useApiToast } from 'components/Toaster';
import { Person } from 'services/person/person.types';
import { useGetPersonMutation } from 'services/person/person.endpoint';
import { Partner } from 'services/partner/partner.types';
import { useGetPartnersMutation } from 'services/partner/partner.endpoint';
import { getDatalists } from 'features/datalist';
import { useSelector } from 'react-redux';
export default function usePersonProjectEventVM() {
    const [person, setPerson] = useState<Person>();
    const [projectEvent, setProjectEvent] = useState<PersonProjectEvent>();
    const [users, setUsers] = useState<User[]>([]);
    const [partners, setPartners] = useState<Partner[]>([]);

    const [lastCreatedEventLabel, setLastCreatedEventLabel] = useState<string>('');
    const [lastUpdatedEventLabel, setLastUpdatedEventLabel] = useState<string>('');
    const { showErrorToast, showApiToast } = useApiToast();

    const datalistData = useSelector(getDatalists);

    const navigate = useNavigate();

    const { projectId, personId, eventId } = useParams();

    const isEdit = eventId != null;

    const [
        getPersonProjectEvent,
        {
            isLoading: isGetPersonProjectEventLoading,
            isError: isGetPersonProjectEventError,
            isSuccess: isGetPersonProjectEventSuccess,
            error: personProjectEventError,
            data: personProjectEventResponse
        }
    ] = useGetPersonProjectEventMutation();

    useEffect(() => {
        if (projectId && eventId) {
            getPersonProjectEvent({ id: parseInt(projectId), eventId: parseInt(eventId) });
        }
    }, [projectId, eventId, getPersonProjectEvent]);

    useEffect(() => {
        if (isGetPersonProjectEventLoading) return;
        if (isGetPersonProjectEventError && personProjectEventError) {
            showErrorToast(personProjectEventError, {
                message: "Erreur de chargement de l'événement"
            });
            return;
        }
        if (isGetPersonProjectEventSuccess && personProjectEventResponse) {
            setProjectEvent(personProjectEventResponse.data);
            return;
        }
    }, [
        isGetPersonProjectEventLoading,
        isGetPersonProjectEventError,
        isGetPersonProjectEventSuccess,
        personProjectEventError,
        personProjectEventResponse,
        showErrorToast
    ]);

    const [
        getPersonDetails,
        {
            isLoading: isGetPersonDetailsLoading,
            isError: isGetPersonDetailsError,
            isSuccess: isGetPersonDetailsSuccess,
            error: personDetailsError,
            data: personDetailsResponse
        }
    ] = useGetPersonMutation();

    useEffect(() => {
        if (personId) {
            getPersonDetails({ id: parseInt(personId) });
        }
    }, [personId, getPersonDetails]);

    useEffect(() => {
        if (isGetPersonDetailsLoading) return;
        if (isGetPersonDetailsError && personDetailsError) {
            showErrorToast(personDetailsError, {
                message: 'Erreur de chargement des types de sources'
            });
            return;
        }
        if (isGetPersonDetailsSuccess && personDetailsResponse) {
            setPerson(personDetailsResponse.data);
            return;
        }
    }, [
        isGetPersonDetailsLoading,
        isGetPersonDetailsError,
        isGetPersonDetailsSuccess,
        personDetailsError,
        personDetailsResponse,
        showErrorToast
    ]);

    const [
        createPersonProjectEvent,
        {
            isLoading: isCreateLoading,
            isError: isCreateError,
            isSuccess: isCreateSuccess,
            error: projectRequestsError,
            data: projectRequestsResponse
        }
    ] = useCreatePersonProjectEventMutation();

    const [
        updatePersonProjectEvent,
        {
            isLoading: isUpdateLoading,
            isError: isUpdateError,
            isSuccess: isUpdateSuccess,
            error: projectEventUpdateError,
            data: projectEventUpdateResponse
        }
    ] = useUpdatePersonProjectEventMutation();

    const [
        getAllUsers,
        { isLoading: isUsersLoading, isError: isUsersError, isSuccess: isUsersSuccess, error: usersError, data: usersResponse }
    ] = useGetAllUsersMutation();

    const [
        getPartners,
        {
            isLoading: isPartnersLoading,
            isError: isPartnersError,
            isSuccess: isPartnersSuccess,
            error: partnersError,
            data: partnersResponse
        }
    ] = useGetPartnersMutation();

    useEffect(() => {
        getAllUsers({});
        getPartners({});
    }, []);

    useEffect(() => {
        if (isUsersLoading) return;
        if (isUsersError && usersError) {
            showErrorToast(usersError, {
                message: 'Erreur de chargement des utilisateurs'
            });
            return;
        }
        if (isUsersSuccess && usersResponse) {
            setUsers(usersResponse.data);
        }
    }, [isUsersLoading, isUsersError, isUsersSuccess, usersError, usersResponse, showErrorToast]);

    useEffect(() => {
        if (isPartnersLoading) return;
        if (isPartnersError && partnersError) {
            showErrorToast(partnersError, {
                message: 'Erreur de chargement des partenaires'
            });
            return;
        }
        if (isPartnersSuccess && partnersResponse) {
            setPartners(partnersResponse.data);
        }
    }, [isPartnersLoading, isPartnersError, isPartnersSuccess, partnersError, partnersResponse, showErrorToast]);

    useEffect(() => {
        if (isCreateLoading) return;
        if (isCreateError && projectRequestsError) {
            showErrorToast(projectRequestsError, {
                message: `Erreur lors de la création de l'événement "${lastCreatedEventLabel}"`
            });
        }
        if (isCreateSuccess && projectRequestsResponse) {
            showApiToast(projectRequestsResponse, {
                successMessage: `Événement "${lastCreatedEventLabel}" créé avec succès`,
                errorMessage: `Erreur lors de la création de l'événement "${lastCreatedEventLabel}"`
            });
            return;
        }
    }, [
        isCreateLoading,
        isCreateError,
        projectRequestsError,
        projectRequestsResponse,
        isCreateSuccess,
        showErrorToast,
        lastCreatedEventLabel,
        showApiToast
    ]);

    useEffect(() => {
        if (isUpdateLoading) return;
        if (isUpdateError && projectEventUpdateError) {
            showErrorToast(projectEventUpdateError, {
                message: `Erreur lors de la mise à jour de l'événement "${lastUpdatedEventLabel}"`
            });
        }
        if (isUpdateSuccess && projectEventUpdateResponse) {
            showApiToast(projectEventUpdateResponse, {
                successMessage: `Événement "${lastUpdatedEventLabel}" mis à jour avec succès`,
                errorMessage: `Erreur lors de la mise à jour de l'événement "${lastUpdatedEventLabel}"`
            });
            return;
        }
    }, [
        isUpdateLoading,
        isUpdateError,
        projectEventUpdateError,
        projectEventUpdateResponse,
        isUpdateSuccess,
        showErrorToast,
        lastUpdatedEventLabel,
        showApiToast
    ]);

    async function handleCreatePersonProjectEvent(data: PersonProjectEventFormik) {
        setLastCreatedEventLabel(data.label);
        const payload: PersonProjectEventPayload = {
            ...data,
            stakeholders: (data.stakeholders_list ? data.stakeholders_list : projectEvent?.stakeholders_list)?.map(
                (stakeholder) => stakeholder.value
            ),
            assistants: (data.assistants_list ? data.assistants_list : projectEvent?.assistants_list)?.map((assistant) => assistant.value),
            partners: (data.partners_list ? data.partners_list : projectEvent?.partners_list)?.map((partner) => partner.value),
            emails: (data.emails_list ? data.emails_list : projectEvent?.emails_list)?.map((email) => email.value)
        } as unknown as PersonProjectEventPayload;

        try {
            if (!isEdit) {
                if (!personId || !projectId) return;
                await createPersonProjectEvent({
                    id: parseInt(projectId),
                    body: {
                        ...payload
                    }
                });
            } else {
                if (!personId || !projectId || !eventId) return;
                setLastUpdatedEventLabel(data.label);
                await updatePersonProjectEvent({
                    projectId: parseInt(projectId),
                    eventId: parseInt(eventId),
                    body: payload
                });
            }
            navigate(`/app/admin/persons/${personId}/project/${projectId}/events`);
        } catch (error) {
            console.error('rejected', error);
        }
    }

    return {
        users,
        partners,
        handleCreatePersonProjectEvent,
        isEdit,
        person,
        projectEvent,
        communicationTypes: datalistData.datalists?.communication_types ? Object.values(datalistData.datalists?.communication_types) : []
    };
}
